import React, { createContext, useContext, useState, useEffect } from "react";
import en from "../lang/en";
import es from "../lang/es";
import { persistClickIds, initFunnel } from "../utils/tracking";

const getCookie = (name) =>
  document.cookie
    .split("; ")
    .find((c) => c.startsWith(name + "="))
    ?.split("=")[1] || "";

// Precedence: a NEW ?r= (fresh ad click) > explicit user choice > affiliate
// cookie > English.
//
// r= wins over a stored preference because a returning visitor who once browsed
// in English would otherwise keep seeing English after clicking a Spanish ad.
// But only a *new* r= overrides: we remember which r last set the language, so a
// deliberate switch via the language switcher still sticks on reload within the
// same campaign (otherwise r= would fight the switcher on every page load).
const LANG_SOURCE_KEY = "lang_r";

export const detectLanguage = () => {
  const stored = localStorage.getItem("lang");
  const r = new URLSearchParams(window.location.search).get("r");

  if (r && r !== localStorage.getItem(LANG_SOURCE_KEY)) {
    const fromR = r.toLowerCase().includes("spanish") ? "es" : "en";
    localStorage.setItem(LANG_SOURCE_KEY, r);
    localStorage.setItem("lang", fromR);
    return fromR;
  }

  if (stored === "en" || stored === "es") return stored;
  if (r) return r.toLowerCase().includes("spanish") ? "es" : "en";
  return getCookie("affiliate").toLowerCase().includes("spanish") ? "es" : "en";
};

// Same cookie format Home.js historically wrote — backend attribution
// (users.affiliate, includes("fb") checks) depends on the raw value.
const persistAffiliate = () => {
  const r = new URLSearchParams(window.location.search).get("r");
  if (!r) return;
  const host = window.location.hostname;
  const maxAge = 60 * 60 * 24 * 30;
  // Leading dot matters: "startviralgpt.com".endsWith("viralgpt.com") is true,
  // so we must match ".viralgpt.com" to avoid the landing domain trying to set
  // a .viralgpt.com cookie the browser would reject.
  const isMainSite = host === "viralgpt.com" || host.endsWith(".viralgpt.com");
  if (host === "localhost") {
    document.cookie = `affiliate=${r};path=/;max-age=${maxAge};SameSite=Lax`;
  } else if (isMainSite) {
    // viralgpt.com — share across subdomains (api, www).
    document.cookie = `affiliate=${r};path=/;domain=.viralgpt.com;max-age=${maxAge};Secure;SameSite=None`;
  } else {
    // Variant landing domain (e.g. startviralgpt.com). A .viralgpt.com cookie
    // would be rejected here and viralgpt.com can't read a startviralgpt.com
    // cookie anyway — so write a host-only cookie purely to keep this domain's
    // own language detection sticky and to feed mainUrl()'s ?r= forwarding.
    document.cookie = `affiliate=${r};path=/;max-age=${maxAge};Secure;SameSite=Lax`;
  }
};

const LanguageContext = createContext({ lang: "en", L: en, setLang: () => {} });

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectLanguage);

  const setLang = (code) => {
    if (code !== "en" && code !== "es") return;
    localStorage.setItem("lang", code);
    setLangState(code);
  };

  useEffect(() => {
    persistAffiliate();
    initFunnel();
    // Capture ttclid/fbclid on every page (not just Home) so a visitor who
    // lands directly on /register, /pricing, etc. still gets the click id
    // cookied for the pixel/CAPI calls on those pages.
    persistClickIds();
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, L: lang === "es" ? es : en, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Returns the active dictionary: const L = useLanguage(); L.faq.heading
export const useLanguage = () => useContext(LanguageContext).L;

// Returns { lang, setLang } for the switcher and DB sync
export const useLang = () => {
  const { lang, setLang } = useContext(LanguageContext);
  return { lang, setLang };
};
